import type { ReactNode } from 'react';
import { X } from 'lucide-react';
import { useConfettiBurst } from '@hooks/useConfettiBurst';
import { useReturnFocusOnClose } from '@hooks/useReturnFocusOnClose';
import { Drawer, DrawerContent, DrawerDescription, DrawerTitle } from '@components/ui/drawer';

/**
 * Radix's open autofocus, onto the sheet itself (its focus scope, tabIndex -1). vaul turns the
 * autofocus off, which left focus on the page under the open sheet, where Enter or Space pressed
 * the control behind it. The APG modal pattern moves focus into the dialog; the sheet rather than
 * a control in it, so no keyboard opens and a stray Enter presses nothing.
 */
const focusSheet = (event: Event): void => {
  event.preventDefault();
  const sheet = event.currentTarget;
  if (sheet instanceof HTMLElement) sheet.focus({ preventScroll: true });
};

interface CelebrationDrawerFrameProps {
  /** Called with `false` when the member closes it: the X, a swipe, a tap outside, Escape. */
  onOpenChange: (open: boolean) => void;
  /**
   * Everything in the sheet below the close X, in order, as direct children of its gap-5 column.
   * The frame renders no title of its own, so this must include a `DrawerTitle`: it is the
   * dialog's accessible name, and Radix warns without one. It usually ends with a
   * `CelebrationDrawerCta`, or with the caller's own controls.
   */
  children: ReactNode;
  /** Where focus goes when the sheet closes. Default (omitted): back to the control that opened it (useReturnFocusOnClose). Pass one when that opener is gone. */
  onCloseAutoFocus?: (event: Event) => void;
}

/**
 * The chrome every celebration shares: the sheet, its close X, a two-cannon canvas-confetti burst
 * on open (`useConfettiBurst`: gated on prefers-reduced-motion, its chunk loaded only when a
 * celebration fires), focus on the sheet as it opens (`focusSheet`), and focus back on the control
 * that opened it when it closes (`useReturnFocusOnClose`). The content is the caller's:
 * `SimpleCelebrationDrawer` for the illustration, title, description and CTA layout, or this frame
 * directly for any other body.
 *
 * It is open for as long as it is mounted: render it when the celebration opens and stop rendering
 * it when it closes. There is no `open` prop to keep it mounted and toggle, because the opener is
 * read when it mounts; a sheet kept mounted would send focus back to whatever had it then.
 */
export function CelebrationDrawerFrame({
  onOpenChange,
  children,
  onCloseAutoFocus,
}: CelebrationDrawerFrameProps) {
  useConfettiBurst(true);
  // Called even when the caller places focus itself: the hook's hand-off bookkeeping stays the same.
  const returnFocusOnClose = useReturnFocusOnClose();

  return (
    <Drawer open onOpenChange={onOpenChange}>
      <DrawerContent
        hideHandle
        // Focused from code as it opens: no ring on the sheet itself; its controls keep theirs.
        className="gap-5 px-5 pb-safe-offset-8 pt-5 outline-hidden"
        onOpenAutoFocus={focusSheet}
        onCloseAutoFocus={onCloseAutoFocus ?? returnFocusOnClose}
      >
        <button
          type="button"
          aria-label="Close"
          onClick={() => {
            onOpenChange(false);
          }}
          // 44px target (ui-ux-designer, Touch Targets); the 24px icon stays 10px from each edge.
          // The ring sits off the X (Focus States: "Always pair a ring with ring-offset-2"); in the
          // corner part of it falls past the sheet's edge, and the visible arc is ink on white.
          className="absolute right-0 top-0 flex size-11 items-center justify-center rounded-full text-black focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mb-teal-ink focus-visible:ring-offset-2"
        >
          <X className="h-6 w-6" aria-hidden />
        </button>
        {children}
      </DrawerContent>
    </Drawer>
  );
}

interface CelebrationDrawerCtaProps {
  onClick: () => void;
  /** The label. */
  children: ReactNode;
}

/**
 * A celebration's single full-width CTA, the last thing in its frame. Its label is ink on the teal
 * fill (TECH-746): 8.37:1 at rest, 5.84:1 on the hover teal and 5.22:1 on the pressed one, which is
 * the feedback a tap gets on touch, where hover never applies. White on this teal is 2.08:1.
 */
export function CelebrationDrawerCta({ onClick, children }: CelebrationDrawerCtaProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="h-12 w-full rounded-full bg-mb-teal font-fira text-base font-medium tracking-button text-mb-ink transition-colors hover:bg-mb-teal-dark active:bg-mb-turquoise-700 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mb-teal-ink focus-visible:ring-offset-2"
    >
      {children}
    </button>
  );
}

export interface SimpleCelebrationDrawerProps {
  /** Called with `false` when the member closes it. Open while mounted, like the frame. */
  onOpenChange: (open: boolean) => void;
  illustrationSrc: string;
  illustrationAlt?: string;
  illustrationClassName?: string;
  title: string;
  description: string;
  ctaLabel?: string;
  /** Defaults to closing the drawer. */
  onCtaClick?: () => void;
}

/**
 * A celebration that is an illustration, a title, a description and one CTA, in the shared frame.
 * The Space activation celebrations render it from their config (`STEP_CELEBRATIONS`).
 */
export function SimpleCelebrationDrawer({
  onOpenChange,
  illustrationSrc,
  illustrationAlt = '',
  illustrationClassName = 'h-22.5 w-21.5',
  title,
  description,
  ctaLabel = 'Keep going',
  onCtaClick,
}: SimpleCelebrationDrawerProps) {
  return (
    <CelebrationDrawerFrame onOpenChange={onOpenChange}>
      <div className="flex flex-col items-center gap-5 pt-2">
        <img src={illustrationSrc} alt={illustrationAlt} className={illustrationClassName} />
        <div className="flex flex-col items-center gap-2.5 text-center">
          <DrawerTitle className="text-lg font-bold text-black">{title}</DrawerTitle>
          <DrawerDescription className="text-sm text-mb-dark-gray">{description}</DrawerDescription>
        </div>
      </div>
      <CelebrationDrawerCta
        onClick={
          onCtaClick ??
          (() => {
            onOpenChange(false);
          })
        }
      >
        {ctaLabel}
      </CelebrationDrawerCta>
    </CelebrationDrawerFrame>
  );
}
